class UGCVideoGrid extends HTMLElement {
  constructor() {
    super();
    this.videos = [];
    this.currentVideoIndex = 0;
    this.isPlaying = false;
    this.flickityInstance = null;
    this.listenersAttached = false;
    this.initialized = false;
  }

  connectedCallback() {
    // Prevent multiple initialization
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Wait for next tick to ensure all child elements are rendered
    setTimeout(() => {
      this.initializeFlickity();
      this.initializeVideos();
      this.setupEventListeners();
      this.startAutoPlay();
    }, 50);
  }

  initializeFlickity() {
    // Find the slide-show element (Flickity carousel)
    const slideShow = this.querySelector('slide-show');
    if (slideShow && slideShow.dataset.initiated === 'true') {
      // Get Flickity instance from the slide-show element
      this.flickityInstance = Flickity.data(slideShow);
      
      if (this.flickityInstance) {
        // Listen for Flickity slide changes
        this.flickityInstance.on('change', (index) => {
          this.onSlideChange(index);
        });
      }
    } else if (slideShow) {
      // Wait for slide-show to be initialized
      setTimeout(() => {
        this.initializeFlickity();
      }, 100);
    }
  }

  onSlideChange(index) {
    // When slide changes via navigation, pause current video and play the new one
    if (index !== this.currentVideoIndex && this.videos[index]) {
      // Pause all videos first
      this.videos.forEach((videoData, i) => {
        if (videoData.isPlaying) {
          this.pauseVideo(i);
        }
      });
      
      // Play the video for the current slide
      setTimeout(() => {
        this.playVideo(index);
      }, 200);
    }
  }

  initializeVideos() {
    const videoElements = this.querySelectorAll('.ugc-videos__video');
    const playButtons = this.querySelectorAll('.ugc-videos__play-btn');
    const muteButtons = this.querySelectorAll('.ugc-videos__mute-btn');

    videoElements.forEach((video, index) => {
      this.videos.push({
        element: video,
        playButton: playButtons[index],
        muteButton: muteButtons[index],
        wrapper: video.closest('.video__wrapper'),
        slideItem: video.closest('.ugc-video-slideItem'),
        isPlaying: false,
        isMuted: true
      });

      // Set initial state
      video.muted = true;
      video.loop = false; // Ensure loop is disabled for autoplay sequence
      video.pause();
      
      // Initialize mute button visibility
      this.updateMuteButtonVisibility(index);
      
      // Hide all videos initially except first
      if (index !== 0) {
        this.videos[index].slideItem.classList.remove('active');
      } else {
        this.videos[index].slideItem.classList.add('active');
      }
    });
  }

  setupEventListeners() {
    // Prevent duplicate event listeners
    if (this.listenersAttached) {
      return;
    }
    this.listenersAttached = true;

    this.videos.forEach((videoData, index) => {
      const { element: video, playButton, muteButton } = videoData;

      // Play/Pause button functionality
      playButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent video click event
        this.togglePlayPause(index);
      });

      // Video click to pause/play
      video.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.togglePlayPause(index);
      });

      // Mute/Unmute button functionality
      muteButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent video click event
        this.toggleMute(index);
      });

      // Video ended event
      video.addEventListener('ended', () => {
        this.onVideoEnded(index);
      });

      // Update button visibility when video plays/pauses
      video.addEventListener('play', () => {
        this.updatePlayButtonVisibility(index, true);
      });

      video.addEventListener('pause', () => {
        this.updatePlayButtonVisibility(index, false);
      });
    });
  }

  startAutoPlay() {
    if (this.videos.length > 0) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.playVideo(0);
        }, 200); // Increased delay to ensure everything is ready
      });
    } else {
      console.log('UGC Video Grid: No videos found for autoplay');
    }
  }

  playVideo(index) {
    if (index >= this.videos.length) {
      return;
    }

    // Navigate to the corresponding slide in Flickity
    if (this.flickityInstance && this.flickityInstance.selectedIndex !== index) {
      this.flickityInstance.select(index, false, true); // Select slide without triggering change event
    }

    // Pause all other videos
    this.videos.forEach((videoData, i) => {
      if (i !== index && videoData.isPlaying) {
        this.pauseVideo(i);
      }
    });

    const videoData = this.videos[index];
    const { element: video, wrapper } = videoData;
    
    // Set active state
    this.setActiveVideo(index);
    
    // Ensure video is ready before playing
    if (video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
      this.attemptPlay(video, videoData, index);
    } else {
      // Wait for video to be ready
      const onLoadedData = () => {
        video.removeEventListener('loadeddata', onLoadedData);
        this.attemptPlay(video, videoData, index);
      };
      video.addEventListener('loadeddata', onLoadedData);
      
      // Force load if needed
      video.load();
    }
  }

  attemptPlay(video, videoData, index) {
    // Ensure video is properly set up
    video.muted = true;
    videoData.isMuted = true;
    
    // Play video
    video.play().then(() => {

      videoData.isPlaying = true;
      this.currentVideoIndex = index;
      this.updatePlayButtonVisibility(index, true);
      this.updateMuteButtonVisibility(index);
    }).catch(error => {
      console.error('UGC Video Grid: Error playing video at index', index, ':', error);
      
      // Try to reload and play again
      setTimeout(() => {

        video.load(); // Force reload
        
        // Wait a bit for load then try again
        setTimeout(() => {
          video.play().then(() => {

            videoData.isPlaying = true;
            this.currentVideoIndex = index;
            this.updatePlayButtonVisibility(index, true);
            this.updateMuteButtonVisibility(index);
          }).catch(retryError => {
            console.error('UGC Video Grid: Retry failed for index', index, ':', retryError);
          });
        }, 200);
      }, 300);
    });
  }

  pauseVideo(index) {
    const videoData = this.videos[index];
    const { element: video } = videoData;

    video.pause();
    videoData.isPlaying = false;
    this.updatePlayButtonVisibility(index, false);
    this.updateMuteButtonVisibility(index);
  }

  togglePlayPause(index) {
    const videoData = this.videos[index];
    
    if (videoData.isPlaying) {
      this.pauseVideo(index);
    } else {
      this.playVideo(index);
    }
  }

  toggleMute(index) {
    const videoData = this.videos[index];
    if (!videoData) {
      console.warn('UGC Video Grid: No video data found for index', index);
      return;
    }

    const { element: video } = videoData;
    if (!video) {
      console.warn('UGC Video Grid: No video element found for index', index);
      return;
    }
    
    video.muted = !video.muted;
    videoData.isMuted = video.muted;
    this.updateMuteButtonVisibility(index);
  }

  onVideoEnded(index) {
    const videoData = this.videos[index];
    videoData.isPlaying = false;
    
    // Reset video to beginning and show poster
    videoData.element.currentTime = 0;
    videoData.element.pause();
    
    // Update button visibility
    this.updatePlayButtonVisibility(index, false);
    
    // Play next video
    const nextIndex = (index + 1) % this.videos.length;
    
    // Use requestAnimationFrame for smoother transition
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Ensure next video is reset before playing
        const nextVideoData = this.videos[nextIndex];
        if (nextVideoData) {
          nextVideoData.element.currentTime = 0;
          nextVideoData.isPlaying = false;
          
          // Navigate to next slide and play video
          if (this.flickityInstance) {
            this.flickityInstance.select(nextIndex, false, true);
          }
          
          setTimeout(() => {
            this.playVideo(nextIndex);
          }, 100);
        } else {
          console.error('UGC Video Grid: Next video data not found at index', nextIndex);
        }
      }, 300); // Reduced delay for faster transition
    });
  }

  setActiveVideo(index) {
    this.videos.forEach((videoData, i) => {
      if (i === index) {
        videoData.slideItem.classList.add('active');
      } else {
        videoData.slideItem.classList.remove('active');
      }
    });
    
    // Ensure the slide is visible in viewport via Flickity
    if (this.flickityInstance && this.flickityInstance.selectedIndex !== index) {
      this.flickityInstance.select(index, false, true);
    }
    
    // Update current video index
    this.currentVideoIndex = index;
  }

  updatePlayButtonVisibility(index, isPlaying) {
    const { playButton } = this.videos[index];
    const playIcon = playButton.querySelector('.ugc-videos__play-icon');

    if (isPlaying) {
      playIcon.style.display = 'none';
      playButton.style.opacity = '0';
      playButton.style.pointerEvents = 'none';
    } else {
      playIcon.style.display = 'flex';
      playButton.style.opacity = '1';
      playButton.style.pointerEvents = 'auto';
    }
  }

  updateMuteButtonVisibility(index) {
    const videoData = this.videos[index];
    if (!videoData || !videoData.muteButton) {
      console.warn('UGC Video Grid: Missing video data or mute button for index', index);
      return;
    }

    const { muteButton, isMuted } = videoData;
    const muteIcon = muteButton.querySelector('.ugc-videos__mute-icon');
    const unmuteIcon = muteButton.querySelector('.ugc-videos__unmute-icon');

    if (!muteIcon || !unmuteIcon) {
      console.warn('UGC Video Grid: Missing mute icons for index', index);
      return;
    }

    // Show/hide mute button based on playing state
    if (videoData.isPlaying) {
      muteButton.style.pointerEvents = 'auto';
      muteButton.style.opacity = '1';
      muteButton.style.visibility = 'visible';
    } else {
      muteButton.style.pointerEvents = 'none';
      muteButton.style.opacity = '0.3';
      muteButton.style.visibility = 'visible'; // Keep visible but disabled
    }

    // Update mute/unmute icon
    if (isMuted) {
      muteIcon.style.display = 'flex';
      unmuteIcon.style.display = 'none';
    } else {
      muteIcon.style.display = 'none';
      unmuteIcon.style.display = 'flex';
    }
  }
}

// Register the custom element
customElements.define('ugc-video-grid', UGCVideoGrid);

// Initialize when DOM is loaded
function initializeUGCGrids() {
  
  // Ensure custom element is defined before proceeding
  if (!customElements.get('ugc-video-grid')) {
    setTimeout(initializeUGCGrids, 100);
    return;
  }
  
  // Find UGC grid sections with carousel display type
  const ugcGrids = document.querySelectorAll('.ugc-grid-layout .multicolumn__inner.display-type--carousel');
  
  ugcGrids.forEach((grid, index) => {
    // Check if already wrapped
    if (grid.closest('ugc-video-grid')) {
      return;
    }
    
    // Check if grid has videos before wrapping
    const videos = grid.querySelectorAll('.ugc-videos__video');
    if (videos.length === 0) {
      return;
    }
    
    // Find the slide-show element within the grid
    const slideShow = grid.querySelector('slide-show');
    if (slideShow) {
      const wrapper = document.createElement('ugc-video-grid');
      slideShow.parentNode.insertBefore(wrapper, slideShow);
      wrapper.appendChild(slideShow);
      
      // Also move the dots if they exist
      const dots = grid.querySelector('.flickity-page-dots');
      if (dots) {
        wrapper.appendChild(dots);
      }
      
      // Move the progress bar if it exists
      const progressBar = grid.querySelector('.flickity-progress--bar');
      if (progressBar) {
        wrapper.appendChild(progressBar);
      }
    }
  });
}

// Initialize only once
let ugcGridsInitialized = false;

function safeInitializeUGCGrids() {
  if (ugcGridsInitialized) {
    return;
  }
  ugcGridsInitialized = true;
  initializeUGCGrids();
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', safeInitializeUGCGrids);

// Also initialize on window load as fallback
window.addEventListener('load', () => {
  setTimeout(safeInitializeUGCGrids, 100);
});
